import type { ComponentType, HTMLAttributes, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import type { CaptureMode } from '@/lib/api/types';

/**
 * The camera screens' frame: viewfinder (CA-03) and rapid session (CA-06),
 * drawn to V-B in plan/smart-capture/wireframes/mobile-fit.html.
 *
 * On a phone the preview runs edge to edge and takes every pixel the
 * controls do not, with no tab bar (`nav-section.ts` `isFullScreenRoute`).
 * The controls sit under it in the bottom third, and they are the
 * bottom-most bar, so they carry the bottom safe-area inset. The screen
 * fills the shell's `min-height: 100svh` column through flex rather than a
 * fixed height, so nothing here disables scrolling.
 *
 * From 40rem the earlier rule still holds: the preview is the 3:4 box
 * capped by width (48vh of width is 64vh of height), with the controls in a
 * column beside it.
 */
export function CameraLayout({
  preview,
  previewProps,
  highlighted = false,
  children,
}: {
  /** Video, crop guide and overlays, positioned inside the preview box. */
  preview: ReactNode;
  /** Extra props for the preview box, the drop target on a desktop. */
  previewProps?: HTMLAttributes<HTMLDivElement>;
  /** Outlines the preview, for a file dragged over it. */
  highlighted?: boolean;
  /** The controls under (phone) or beside (tablet up) the preview. */
  children: ReactNode;
}) {
  return (
    <div className="flex min-h-0 flex-1 flex-col sm:flex-row sm:items-center sm:justify-center sm:gap-6 sm:p-6 lg:p-0">
      <div
        {...previewProps}
        data-camera-preview=""
        className={cn(
          'relative min-h-0 flex-1 overflow-hidden bg-black sm:aspect-[3/4] sm:w-full sm:min-w-0 sm:max-w-[48vh] sm:flex-initial sm:rounded-card sm:border sm:border-border lg:max-w-[54vh]',
          highlighted && 'ring-2 ring-inset ring-accent',
        )}
      >
        {preview}
      </div>
      <div
        className="flex shrink-0 flex-col gap-1 px-4 pb-[max(0.5rem,env(safe-area-inset-bottom))] pt-2 sm:w-56 sm:gap-4 sm:px-0 sm:pt-0"
      >
        {children}
      </div>
    </div>
  );
}

/**
 * The 3:4 region the shutter stores, centred in the preview at the largest
 * size that fits, and the shutter cuts the same rectangle from the video
 * (`lib/camera/crop.ts`). An SVG with a 3 by 4 viewBox and
 * `xMidYMid meet` is that fit by definition, so the guide needs no measuring
 * and no container units. The area outside it is dimmed by the evenodd path
 * drawn past the viewBox and clipped by the preview; on the 3:4 box from
 * 40rem up the guide covers the whole preview and dims nothing.
 */
export function CropGuide() {
  return (
    <svg
      aria-hidden="true"
      data-crop-guide=""
      viewBox="0 0 3 4"
      preserveAspectRatio="xMidYMid meet"
      className="pointer-events-none absolute inset-0 size-full overflow-visible"
    >
      <path d="M-1000 -1000H1003V1004H-1000Z M0 0V4H3V0Z" fillRule="evenodd" fill="rgb(0 0 0 / 0.4)" />
      <rect
        x="0"
        y="0"
        width="3"
        height="4"
        fill="none"
        stroke="rgb(255 255 255 / 0.4)"
        strokeWidth="1"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

const OVERLAY_CONTROL =
  'relative flex size-11 shrink-0 items-center justify-center rounded-control border border-border-strong bg-surface/80 text-surface-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent disabled:opacity-50';

type Icon = ComponentType<{ className?: string; 'aria-hidden'?: boolean | 'true' | 'false' }>;

/** A 44px control on the preview: Flip, Mic, Tips and the like. */
export function OverlayButton({
  label,
  icon: IconComponent,
  onClick,
  pressed,
  marked = false,
  disabled,
}: {
  label: string;
  icon: Icon;
  onClick: () => void;
  pressed?: boolean;
  /** Shows a dot on the control, for something it holds. */
  marked?: boolean;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      aria-pressed={pressed}
      disabled={disabled}
      className={cn(OVERLAY_CONTROL, pressed === false && 'text-muted')}
    >
      <IconComponent className="size-5" aria-hidden="true" />
      {marked && (
        <span aria-hidden="true" className="absolute right-1.5 top-1.5 size-2 rounded-full bg-success" />
      )}
    </button>
  );
}

/** The 44px Back control on the preview, a link so it works with no script state. */
export function OverlayLink({ to, label, icon: IconComponent }: { to: string; label: string; icon: Icon }) {
  return (
    <Link to={to} aria-label={label} className={OVERLAY_CONTROL}>
      <IconComponent className="size-5" aria-hidden="true" />
    </Link>
  );
}

/** The round shutter, the screen's primary action, centred in the bottom third. */
export function ShutterButton({
  label,
  onClick,
  disabled,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      data-primary-action=""
      className="flex size-[72px] shrink-0 items-center justify-center rounded-full border-4 border-surface-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-surface disabled:opacity-50"
    >
      <span className="size-14 rounded-full bg-accent" />
    </button>
  );
}

export type CameraModeChoice = Extract<CaptureMode, 'photo' | 'document' | 'burst'>;

const MODES: { mode: CameraModeChoice; label: string }[] = [
  { mode: 'document', label: 'Document' },
  { mode: 'photo', label: 'Single' },
  { mode: 'burst', label: 'Rapid' },
];

/** Document, Single and Rapid, the way a phone's own camera app lists its modes. */
export function ModeSwitch({
  active,
  onSelect,
}: {
  active: CameraModeChoice;
  onSelect: (mode: CameraModeChoice) => void;
}) {
  return (
    <div role="group" aria-label="Mode" className="flex justify-center gap-2 sm:justify-start">
      {MODES.map(({ mode, label }) => (
        <button
          key={mode}
          type="button"
          aria-pressed={mode === active}
          onClick={() => onSelect(mode)}
          className={cn(
            'min-h-11 min-w-16 rounded-control px-3 text-sm text-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
            mode === active && 'font-semibold text-surface-foreground underline decoration-accent decoration-2 underline-offset-8',
          )}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

/** A 56px square control beside the shutter, icon over a one-word label. */
export function SideControl({
  label,
  icon: IconComponent,
  onClick,
  disabled,
  primary = false,
}: {
  label: string;
  icon: Icon;
  onClick: () => void;
  disabled?: boolean;
  primary?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'flex size-14 shrink-0 flex-col items-center justify-center gap-0.5 rounded-control border text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent disabled:opacity-50',
        primary ? 'border-brand bg-brand text-brand-foreground' : 'border-border-strong text-surface-foreground',
      )}
    >
      <IconComponent className="size-5" aria-hidden="true" />
      {label}
    </button>
  );
}
